import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { OnboardingWidgetState } from 'anchor-scout-common/types';
import { FullRow, ProgressBar, Typography, Collapse, useDevice } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import BigConfirmationCheckmarkIcon from './BigConfirmationCheckmarkIcon.json';
import styles from './BusinessSetupWidget.module.scss';
import HelpCard from './HelpCard/HelpCard';
import StepItemsList from './StepItemsList/StepItemsList';
import { useSetupSteps } from './StepsData';
const initialExpandedState = {
    [OnboardingWidgetState.ShouldReveal]: false,
    [OnboardingWidgetState.ShouldDisappear]: true,
    [OnboardingWidgetState.StaticOpened]: true,
    [OnboardingWidgetState.Hidden]: false
};
const START_ANIMATION_DELAY = 1000;
const COMPLETED_ANIMATION_DELAY = 3000;
const FIRST_STEP_COMPLETED_DELAY = 200;
const END_COLLAPSED_DELAY = 600;
export default function BusinessSetupWidget(param) {
    let { viewingState, onAnimationEnd, testAnimationsDelay } = param;
    const { isMobileDevice } = useDevice();
    const { Lottie } = useIoContext();
    const stepsToDo = useSetupSteps();
    const [expanded, setExpanded] = useState(initialExpandedState[viewingState]);
    const [showEndAnimation, setShowEndAnimation] = useState(false);
    const [revealCompleted, setRevealCompleted] = useState(viewingState !== OnboardingWidgetState.ShouldReveal);
    const [showCompletedText, setShowCompletedText] = useState(false);
    const { t } = useLocaleKeys('pages.dashboard.setupWidget.createYourAccount');
    const { t: widgetTexts } = useLocaleKeys('pages.dashboard.setupWidget');
    const createYourAccountStep = useMemo(()=>({
            title: t.title(),
            status: revealCompleted ? 'completed' : 'initial'
        }), [
        revealCompleted,
        t
    ]);
    const steps = useMemo(()=>[
            createYourAccountStep,
            ...stepsToDo
        ], [
        createYourAccountStep,
        stepsToDo
    ]);
    const total = steps.length;
    const completed = useMemo(()=>steps.reduce((acc, s)=>acc + (s.status === 'completed' ? 1 : 0), 0), [
        steps
    ]);
    const isActionRequired = useMemo(()=>completed < total, [
        completed,
        total
    ]);
    const startRevealAnimation = useCallback(()=>{
        setTimeout(()=>{
            setExpanded(true);
            onAnimationEnd(true);
        }, testAnimationsDelay !== null && testAnimationsDelay !== void 0 ? testAnimationsDelay : START_ANIMATION_DELAY);
    }, [
        testAnimationsDelay,
        onAnimationEnd
    ]);
    const startEndAnimation = useCallback(()=>{
        setTimeout(()=>{
            setShowEndAnimation(true);
        }, testAnimationsDelay !== null && testAnimationsDelay !== void 0 ? testAnimationsDelay : START_ANIMATION_DELAY);
    }, [
        testAnimationsDelay
    ]);
    useEffect(()=>{
        if (viewingState === OnboardingWidgetState.ShouldReveal) {
            setRevealCompleted(false);
            startRevealAnimation();
        } else if (viewingState === OnboardingWidgetState.ShouldDisappear) {
            startEndAnimation();
        } else if (viewingState === OnboardingWidgetState.Hidden && isActionRequired) {
            startRevealAnimation();
        }
    }, [
        startEndAnimation,
        startRevealAnimation,
        viewingState,
        isActionRequired
    ]);
    useEffect(()=>{
        if (!isActionRequired) {
            startEndAnimation();
        }
    }, [
        isActionRequired,
        startEndAnimation
    ]);
    const checkmarkAnimation = useMemo(()=>/*#__PURE__*/ _jsx(Lottie, {
            animationData: BigConfirmationCheckmarkIcon,
            loop: false,
            autoplay: true,
            onComplete: ()=>{
                setShowCompletedText(true);
                setTimeout(()=>{
                    setExpanded(false);
                }, testAnimationsDelay !== null && testAnimationsDelay !== void 0 ? testAnimationsDelay : COMPLETED_ANIMATION_DELAY);
            },
            style: {
                width: 240,
                height: 180
            }
        }), [
        testAnimationsDelay
    ]);
    const allStepsCompletedAnimation = useMemo(()=>/*#__PURE__*/ _jsxs("div", {
            className: styles.endAnimation,
            "data-testid": "end-animation",
            children: [
                checkmarkAnimation,
                /*#__PURE__*/ _jsx(Typography, {
                    className: cn(styles.completedText, {
                        [styles.visible]: showCompletedText
                    }, styles.congratsTitle),
                    variant: "h4",
                    children: "Congrats! Your setup is done"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    className: cn(styles.completedSubText, {
                        [styles.visible]: showCompletedText
                    }),
                    children: "You’re all set to run your business with Anchor"
                })
            ]
        }), [
        checkmarkAnimation,
        showCompletedText
    ]);
    if (viewingState === OnboardingWidgetState.Hidden && !isActionRequired) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.widgetContainer,
        "data-testid": "business-setup-widget-container",
        children: /*#__PURE__*/ _jsx(Collapse, {
            timeout: testAnimationsDelay !== null && testAnimationsDelay !== void 0 ? testAnimationsDelay : END_COLLAPSED_DELAY,
            in: expanded,
            onEntered: ()=>setTimeout(()=>setRevealCompleted(true), testAnimationsDelay !== null && testAnimationsDelay !== void 0 ? testAnimationsDelay : FIRST_STEP_COMPLETED_DELAY),
            onExited: ()=>onAnimationEnd(false),
            children: showEndAnimation && !isActionRequired ? allStepsCompletedAnimation : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        className: styles.mainSubtitle,
                        weight: "normal",
                        variant: "h6",
                        testId: "title",
                        style: {
                            color: colors.dark800
                        },
                        children: widgetTexts.title()
                    }),
                    /*#__PURE__*/ _jsxs(FullRow, {
                        gap: 20,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.stepItemsListContainer,
                                children: [
                                    /*#__PURE__*/ _jsx(ProgressBar, {
                                        className: styles.progressBar,
                                        completed: completed,
                                        total: total
                                    }),
                                    /*#__PURE__*/ _jsx(StepItemsList, {
                                        steps: steps
                                    })
                                ]
                            }),
                            !isMobileDevice && /*#__PURE__*/ _jsx(HelpCard, {
                                testId: "help-card"
                            })
                        ]
                    })
                ]
            })
        })
    });
}
